/**
 * Explore filter state — Zustand store.
 *
 * Category / location / dates / filters are persisted so the user sees the
 * same view after a tab switch or refresh. `isFilterSheetOpen` is the one
 * field that should NEVER rehydrate (a dangling open sheet looks broken on
 * startup), so it's excluded from the persisted slice.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { VehicleCategory, UserLocation, TransmissionType } from '../types/vehicle.types'
import { createPlatformStorage } from '../storage/zustand-storage'

const FILTER_DEFAULTS = {
  transmission: null as TransmissionType | null,
  minPriceCents: null as number | null,
  maxPriceCents: null as number | null,
  minYear: null as number | null,
  isInstantBook: null as boolean | null,
  requiredFeatures: [] as string[],
}

/**
 * Where Explore looks: the whole country, or around the person.
 *
 * `'all'` is the default and it is a product decision, not a fallback. The app
 * used to ask for location on mount and filter to 50 km of whatever it got, so
 * DENYING the permission gave you a working app and GRANTING it could give you
 * an empty one — every vehicle is in San Salvador, so anyone opening it from
 * abroad saw nothing. Now the first screen shows the country and the permission
 * is asked for only when someone taps "Cerca de mí", at the moment the reason
 * is obvious.
 */
export type LocationScope = 'all' | 'nearby'

interface ExploreStoreState {
  category: VehicleCategory | null
  location: UserLocation | null
  locationScope: LocationScope
  pickupDate: string | null
  returnDate: string | null
  query: string
  transmission: TransmissionType | null
  minPriceCents: number | null
  maxPriceCents: number | null
  minYear: number | null
  isInstantBook: boolean | null
  requiredFeatures: string[]
  isFilterSheetOpen: boolean

  setCategory: (c: VehicleCategory | null) => void
  setLocation: (loc: UserLocation | null) => void
  setLocationScope: (scope: LocationScope) => void
  setDates: (pickup: string | null, ret: string | null) => void
  setQuery: (query: string) => void
  setTransmission: (t: TransmissionType | null) => void
  setPriceRange: (min: number | null, max: number | null) => void
  setMinYear: (y: number | null) => void
  setInstantBook: (v: boolean | null) => void
  setRequiredFeatures: (features: string[]) => void
  setFilterSheetOpen: (open: boolean) => void
  resetFilters: () => void
  reset: () => void
}

type PersistedExplore = Omit<
  ExploreStoreState,
  | 'isFilterSheetOpen'
  | 'setCategory'
  | 'setLocation'
  | 'setLocationScope'
  | 'setDates'
  | 'setQuery'
  | 'setTransmission'
  | 'setPriceRange'
  | 'setMinYear'
  | 'setInstantBook'
  | 'setRequiredFeatures'
  | 'setFilterSheetOpen'
  | 'resetFilters'
  | 'reset'
>

export const useExploreStore = create<ExploreStoreState>()(
  persist(
    (set) => ({
      category: null,
      location: null,
      locationScope: 'all',
      pickupDate: null,
      returnDate: null,
      query: '',
      ...FILTER_DEFAULTS,
      isFilterSheetOpen: false,

      setCategory: (category) => set({ category }),
      setLocation: (location) => set({ location }),
      setLocationScope: (locationScope) => set({ locationScope }),
      setDates: (pickupDate, returnDate) => set({ pickupDate, returnDate }),
      setQuery: (query) => set({ query }),
      setTransmission: (transmission) => set({ transmission }),
      setPriceRange: (minPriceCents, maxPriceCents) => set({ minPriceCents, maxPriceCents }),
      setMinYear: (minYear) => set({ minYear }),
      setInstantBook: (isInstantBook) => set({ isInstantBook }),
      setRequiredFeatures: (requiredFeatures) => set({ requiredFeatures }),
      setFilterSheetOpen: (isFilterSheetOpen) => set({ isFilterSheetOpen }),
      // "Limpiar" has to clear the category too. The filter sheet never shows
      // the category chip, so this is the only control that can undo it — and
      // leaving it set meant the results stayed filtered after the user
      // explicitly cleared the filters. A free-text query is a filter just
      // like the rest: "Limpiar" empties it even though it types elsewhere.
      resetFilters: () =>
        set({ category: null, query: '', ...FILTER_DEFAULTS, isFilterSheetOpen: false }),
      reset: () =>
        set({
          category: null,
          location: null,
          locationScope: 'all',
          pickupDate: null,
          returnDate: null,
          query: '',
          ...FILTER_DEFAULTS,
          isFilterSheetOpen: false,
        }),
    }),
    {
      name: 'tago.explore.v1',
      storage: createPlatformStorage<PersistedExplore>(),
      // Persist the "sticky" preferences; leave dates + location ephemeral so a
      // stale date doesn't leak across sessions and show a pre-selected range
      // in the past when the user reopens the app.
      partialize: (state): PersistedExplore => ({
        category: state.category,
        location: null,
        // Pinned, because the pill that used to set it is gone: the explore
        // screen already has a country-wide carousel and a nearby one below it,
        // so the control was a third way to say the same thing.
        locationScope: 'all',
        pickupDate: null,
        returnDate: null,
        query: state.query,
        transmission: state.transmission,
        minPriceCents: state.minPriceCents,
        maxPriceCents: state.maxPriceCents,
        minYear: state.minYear,
        isInstantBook: state.isInstantBook,
        requiredFeatures: state.requiredFeatures,
      }),

      /*
       * v1 → v2 exists to retire a value nothing can set any more.
       *
       * Pinning it in `partialize` above was not enough, and the gap is worth
       * naming because it is easy to get wrong twice: `partialize` shapes what
       * is WRITTEN. On startup zustand reads the raw stored object and merges it
       * OVER the initial state, so a phone that tapped "Cerca de mí" in the
       * shipped build boots this update with 'nearby' restored — and the
       * reconciliation that used to correct it lived inside the deleted control.
       *
       * The result was not a broken search: `useVehicles` degrades to the whole
       * country when there is no coordinate, and there is no longer anything
       * that can produce one. It was a lie on screen. With scope 'nearby' and no
       * fallback flagged, explore titles the country-wide list "Cerca de vos" —
       * precisely what that title's own comment says it must never do.
       *
       * A version bump is what makes zustand take this branch AND write the
       * cleaned state back to disk; `merge` alone would fix the session and
       * leave the bad value on the device. Every other sticky preference is
       * carried through untouched.
       */
      version: 2,
      migrate: (persisted): PersistedExplore => ({
        ...(persisted as PersistedExplore),
        locationScope: 'all',
      }),
    },
  ),
)
